import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from "@tanstack/react-table";
import { Box, CircularProgress, Typography } from "@mui/material";

import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";
import useTableInstance from "hooks/useTableInstance";
import TableField from "components/Table";
import SearchInput from "components/SearchInput";
import { EVENTS_LOG, EVENTS_LOG_MAP, SEVERITY_LEVEL } from "const/eventsLog";
import { formatTimestamp } from "utils/format";
import getEventsLogMessage from "utils/getEventsLogMessage";

const severityLabel = {
  critical: "Critical",
  warning: "Warning",
  info: "Information",
};

const formatLogTime = (timestamp) =>
  formatTimestamp(timestamp, "-").slice(0, 16) || "-";

const Logs = ({ timeRange }) => {
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [requestUuid] = useState(uuidv4());
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthReady) return;

    const summaryReportId = timeRange?.summaryReportId || "mock-summary-report";
    const apiKey = standalone
      ? "/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/logs.read"
      : "/ents/:entId/orgs/:orgId/sites/:siteId/summaryReports/:summaryReportId/logs.read";
    const restfulPlaceholders = standalone
      ? { orgId: auth.realm?.id, siteId: auth.sites?.id, summaryReportId }
      : {
          entId: auth.realm?.id,
          orgId: auth.orgs?.id,
          siteId: auth.sites?.id,
          summaryReportId,
        };

    const load = async () => {
      setLoading(true);
      const { getSendBatchData } = await sendBatch([
        { apiKey, requestUuid, restfulPlaceholders, params: {} },
      ]);
      setLogs(getSendBatchData({ requestUuid })?.data || []);
      setLoading(false);
    };

    load();
  }, [auth, isAuthReady, requestUuid, sendBatch, standalone, timeRange]);

  const rows = useMemo(
    () =>
      logs.map((log) => ({
        ...log,
        time: formatLogTime(log.createdAt),
        severityLabel: severityLabel[SEVERITY_LEVEL[log.severity]] || "-",
        message: getEventsLogMessage(log, EVENTS_LOG, EVENTS_LOG_MAP),
      })),
    [logs],
  );
  const columnHelper = createColumnHelper();
  const columns = useMemo(
    () => [
      columnHelper.accessor("time", {
        header: "Time",
        enableSorting: true,
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("severityLabel", {
        header: "Severity",
        enableSorting: true,
        cell: (info) => <Box>{info.getValue()}</Box>,
      }),
      columnHelper.accessor("message", {
        header: "Message",
        enableSorting: true,
        cell: (info) => (
          <Box sx={{ whiteSpace: "pre-wrap" }}>{info.getValue()}</Box>
        ),
      }),
    ],
    [],
  );
  const tableInstance = useTableInstance({
    tableName: "summaryReportLogs",
    data: rows,
    columns,
    getRowId: (row) => row.id,
    enableSorting: true,
    enablePagination: true,
    enableGlobalFilter: true,
    globalFilterMode: "column",
    globalFilterColumns: ["time", "severityLabel", "message"],
  });
  const shownCount = tableInstance.table.getFilteredRowModel().rows.length;

  return (
    <Box sx={{ mx: "20px", mb: "20px" }}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "16px" }}
      >
        <SearchInput
          value={tableInstance.globalFilter}
          onChange={tableInstance.setGlobalFilter}
          placeholder='Search time, severity, message'
        />
        <Typography sx={{ color: "#373737", fontSize: "13px" }}>
          Showing {shownCount}/{logs.length} logs
        </Typography>
      </Box>
      <TableField
        {...tableInstance}
        pendingChildren={
          loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Typography sx={{ color: "#AAAAAA", py: 3 }}>
              No data available
            </Typography>
          )
        }
      />
    </Box>
  );
};

export default Logs;
